/**
 * Pure URL builders shared by the row dropdown
 * (``InvoiceActionMenu``), the detail-page button row
 * (``InvoiceActionButtons``), and the list-page bulk toolbar
 * (``InvoiceBulkActionBar``). Keeping the definitions in one place
 * avoids the 3-way drift hazard noted in the invoice-group-actions
 * code review.
 */

export function payUrlFor(invoiceId: string): string {
  const origin =
    typeof window === "undefined" ? "" : window.location.origin
  return `${origin}/pay/${invoiceId}`
}

/**
 * The customer self-service portal link a Manager copies from the invoice
 * (docs/design/customer-portal.md). Carries the ``invoice`` id — not the raw
 * email — so the pasted URL leaks no PII; the portal resolves the address
 * server-side and emails the customer a one-time sign-in link.
 */
export function portalUrlFor(invoiceId: string): string {
  const origin =
    typeof window === "undefined" ? "" : window.location.origin
  return `${origin}/portal?invoice=${encodeURIComponent(invoiceId)}`
}

export function pdfUrlFor(invoiceId: string): string {
  // Trailing slash before the query string matches the Ninja-registered
  // route and the Next.js dev-proxy rewrite (which appends ``/`` to
  // every ``/api/*`` forward — without the slash, the request falls
  // through to ``/invoices/{invoice_id}/`` and 422s).
  return `/api/invoices/invoice.pdf/?invoice_id=${encodeURIComponent(invoiceId)}`
}

/**
 * Per-leg deposit receipt download URL. The backend gates this on the
 * leg being ``paid`` (deposit-payments spec §6.7); callers must only
 * surface it once ``leg.status === "paid"``. ``stage`` is
 * ``"deposit"`` | ``"balance"``.
 */
export function depositReceiptUrlFor(
  invoiceId: string,
  stage: "deposit" | "balance",
): string {
  // Trailing slash before the query string, same as ``pdfUrlFor`` — the
  // route is registered as ``receipt.pdf/`` and the dev proxy appends
  // ``/`` to ``/api/*`` forwards; without it the request falls through.
  return `/api/invoices/receipt.pdf/?invoice_id=${encodeURIComponent(
    invoiceId,
  )}&stage=${encodeURIComponent(stage)}`
}

/**
 * The customer-facing per-leg pay URL: ``/pay/{id}/{stage}``
 * (deposit-payments spec §5.3). The rep copies both legs' links from
 * the deposit panel.
 */
export function depositPayUrlFor(
  invoiceId: string,
  stage: "deposit" | "balance",
): string {
  // Compose the per-leg path onto the shared one-shot pay URL so the
  // SSR-safe origin handling lives in one place (``payUrlFor``).
  return `${payUrlFor(invoiceId)}/${stage}`
}
